import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Separator } from "@/components/ui/separator";
import { Edit, Save, X, Percent, DollarSign } from "lucide-react";
import { useCommissionTypes } from "@/hooks/useCommissionTypes";
import { CommissionType } from "@/types/commission";
import { Skeleton } from "@/components/ui/skeleton";

interface EditableCommissionTypeProps {
  commissionType: CommissionType;
  onUpdate: (id: string, percentage: number) => Promise<void>;
}

const EditableCommissionType: React.FC<EditableCommissionTypeProps> = ({ 
  commissionType, 
  onUpdate 
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(commissionType.percentage.toString());
  const [isUpdating, setIsUpdating] = useState(false);

  const handleSave = async () => {
    const newPercentage = parseFloat(editValue);
    
    if (isNaN(newPercentage) || newPercentage < 0 || newPercentage > 100) {
      alert("El porcentaje debe ser un número entre 0 y 100");
      return;
    }

    try {
      setIsUpdating(true);
      await onUpdate(commissionType.id, newPercentage);
      setIsEditing(false);
    } catch (error) {
      console.error("Error al actualizar:", error);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleCancel = () => {
    setEditValue(commissionType.percentage.toString());
    setIsEditing(false);
  };

  return (
    <div className="flex items-center justify-between p-4 border border-border rounded-lg bg-card hover:bg-muted/50 transition-colors">
      <div className="flex-1">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-primary/10 rounded-lg">
            <DollarSign className="h-5 w-5 text-primary" />
          </div>
          <div>
            <h3 className="font-semibold text-foreground">{commissionType.display_name}</h3>
            <p className="text-sm text-muted-foreground">{commissionType.description}</p>
          </div>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        {isEditing ? (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              className="w-20 text-center"
              min="0"
              max="100"
              step="0.01"
            />
            <span className="text-sm text-muted-foreground">%</span>
            <Button
              size="sm"
              onClick={handleSave}
              disabled={isUpdating}
              className="h-8 w-8 p-0"
            >
              <Save className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={handleCancel}
              disabled={isUpdating}
              className="h-8 w-8 p-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="px-3 py-1">
              <Percent className="h-3 w-3 mr-1" />
              {commissionType.percentage}%
            </Badge>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsEditing(true)}
              className="h-8 w-8 p-0"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

const CommissionTypesManager: React.FC = () => {
  const { 
    commissionTypes, 
    loading, 
    error, 
    updateCommissionType 
  } = useCommissionTypes();

  const handleUpdatePercentage = async (id: string, percentage: number) => {
    await updateCommissionType(id, { percentage });
  };

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Comisiones</CardTitle>
          <CardDescription>
            Gestiona los porcentajes de comisión por tipo de cliente
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-20 w-full" />
          ))}
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Configuración de Comisiones</CardTitle>
        </CardHeader>
        <CardContent>
          <Alert variant="destructive">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Percent className="h-5 w-5" />
          Configuración de Comisiones
        </CardTitle>
        <CardDescription>
          Configura los porcentajes de comisión según el tipo de cliente. 
          Estos porcentajes se aplicarán automáticamente cuando un vendedor asigne un cliente a cada categoría.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          <div className="grid gap-4">
            {commissionTypes.map((type, index) => (
              <React.Fragment key={type.id}>
                <EditableCommissionType
                  commissionType={type}
                  onUpdate={handleUpdatePercentage}
                />
                {index < commissionTypes.length - 1 && <Separator />}
              </React.Fragment>
            ))}
          </div>
          
          {commissionTypes.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <Percent className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No hay tipos de comisión configurados</p>
            </div>
          )}
        </div>
        
        <div className="mt-6 p-4 bg-muted/50 border border-border rounded-lg">
          <h4 className="font-medium text-foreground mb-2">💡 Cómo funciona:</h4>
          <ul className="text-sm text-muted-foreground space-y-1">
            <li>• <strong>Agencias:</strong> Para clientes que son agencias de publicidad</li>
            <li>• <strong>Clientes Directos:</strong> Para clientes sin intermediarios</li>
            <li>• <strong>Agencia + Volumen:</strong> Para agencias con descuentos especiales por volumen</li>
          </ul>
        </div>
      </CardContent>
    </Card>
  );
};

export default CommissionTypesManager; 